/**
 * web/src/screens/Notebook/icons.tsx — the Notebook card glyphs (card
 * vncVrGk1hc9i: moved out of index.tsx unchanged).
 */
import type { ReactNode, SVGProps } from 'react';
import type { EntryType } from './types';

export const ICON_ATTRS: SVGProps<SVGSVGElement> = {
  xmlns: 'http://www.w3.org/2000/svg',
  width: 14,
  height: 14,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true as const,
  focusable: 'false' as const,
};

export const ENTRY_ICON: Record<EntryType, ReactNode> = {
  link: (
    <svg {...ICON_ATTRS}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  gift: (
    <svg {...ICON_ATTRS}>
      <polyline points="20 12 20 22 4 22 4 12" />
      <rect x="2" y="7" width="20" height="5" />
      <line x1="12" y1="22" x2="12" y2="7" />
      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
    </svg>
  ),
  memory: (
    <svg {...ICON_ATTRS}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  date: (
    <svg {...ICON_ATTRS}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  note: (
    <svg {...ICON_ATTRS}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  idea: (
    <svg {...ICON_ATTRS}>
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M12 2a6 6 0 0 0-3.4 10.9c.8.6 1.4 1.6 1.4 2.6h4c0-1 .6-2 1.4-2.6A6 6 0 0 0 12 2z" />
    </svg>
  ),
};

/** PRD E1/K1 — the pin that leads a place entity chip. One glyph, drawn on
 *  the chip's tint (D2 keeps chips out of the accent roles), so a card that
 *  resolved a place reads as a place without spending a word of text. */
export const PLACE_PIN: ReactNode = (
  <svg {...ICON_ATTRS} width={12} height={12}>
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

/** A transcription card (a0ABwZvTf9ct) keeps `entry_type: note` — a new
 *  entry_type would need a CHECK-constraint migration — and is recognised by
 *  `provider: 'meeting'`, which only the server-side /api/transcribe route
 *  writes. So the meeting gets its own label and icon outside the Record. */
export const MEETING_ICON: ReactNode = (
  <svg {...ICON_ATTRS}>
    <rect x="9" y="2" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <line x1="12" y1="18" x2="12" y2="22" />
  </svg>
);
